/* eslint-disable no-empty */
import type { PublicType } from '@/types/publicType';
import { nextTick } from 'vue';
import useOrigin from './config';

/**
 * 设置KM格式化，处理大数字显示（优化截断版）
 * @param {*} num 需要格式化的数值
 * @param {*} decimal 小数位数，默认保留一位小数
 * @param {*} lang 语言代码，阿语需要特殊处理单位位置
 * @returns 格式化后的数值字符串
 */
export const setKM = (num: string | number | null | undefined, decimal: number = 1, lang?: string): string | number | null | undefined => {
  // 处理特殊情况
  if (num === null || num === undefined || num === 0) return num;
  if (typeof num === 'string' && num.includes('*')) return num;

  // 统一转换为数字
  const numValue = Number(num);
  if (isNaN(numValue)) return num; // 非数字直接返回原值

  // 处理负数
  if (numValue < 0) {
    return `-${setKM(Math.abs(numValue), decimal, lang)}`;
  }

  // 自定义小数截取函数
  const truncateDecimal = (n: number, digits: number): string => {
    const factor = Math.pow(10, digits);
    // 先放大后取整再缩小，避免浮点误差
    const truncated = Math.floor(n * factor) / factor;
    // 分割整数和小数部分
    const [intPart, decimalPart = ''] = truncated.toString().split('.');
    // 补全小数点后的零
    return decimalPart.length >= digits ? truncated.toString() : `${intPart}.${decimalPart.padEnd(digits, '0')}`;
  };

  const k = 1000;
  const m = 1000000;

  // 小于1000的数字处理
  if (numValue < k) {
    const hasDecimal = numValue % 1 !== 0;
    return hasDecimal ? truncateDecimal(numValue, decimal) : numValue.toString();
  }

  // 计算单位和缩放比例
  const isThousands = numValue >= k && numValue < m;
  const scale = isThousands ? k : m;
  const unit = isThousands ? 'k' : 'm';
  const scaledValue = numValue / scale;

  // 截取小数位（非四舍五入）
  const formattedValue = truncateDecimal(scaledValue, decimal);

  // 处理阿语单位位置
  return lang === 'ar' ? `${unit}${formattedValue}` : `${formattedValue}${unit}`;
};

export function checkVersion() {
  const u = navigator.userAgent,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    app = navigator.appVersion;
  return {
    trident: u.indexOf('Trident') > -1, //IE内核
    presto: u.indexOf('Presto') > -1, //opera内核
    webKit: u.indexOf('AppleWebKit') > -1, //苹果、谷歌内核
    gecko: u.indexOf('Gecko') > -1 && u.indexOf('KHTML') == -1, //火狐内核
    mobile: !!u.match(/AppleWebKit.*Mobile.*/), //是否为移动终端
    ios: !!u.match(/\(i[^;]+;( U;)? CPU.+Mac OS X/), //ios终端
    android: u.indexOf('Android') > -1 || u.indexOf('Adr') > -1, //android终端
    iPhone: u.indexOf('iPhone') > -1, //是否为iPhone或者QQHD浏览器
    iPad: u.indexOf('iPad') > -1, //是否iPad
    webApp: u.indexOf('Safari') == -1, //是否web应该程序，没有头部与底部
    weixin: u.indexOf('MicroMessenger') > -1, //是否微信
    qq: !!u.match(/\sQQ/i), //是否QQ
    app: u.indexOf('miparty') > -1 || u.indexOf('Miparty') > -1 || u.indexOf('bobbo') > -1, //是否在app内
  };
}

// var browser = checkVersion()

const info: PublicType.Info = {
  uid: '10000',
  ticket: 't123456',
  lang: 'en',
};
// 判断当前浏览器是否支持webp
export const isLoadMinHeaderWebp = () => {
  return new Promise((resolve) => {
    window.isSupportWebp = false; //是否支持
    const img = new Image();
    function getResult(event: any) {
      //如果进入加载且图片宽度为1(通过图片宽度值判断图片是否可以显示)
      window.isSupportWebp = event && event.type === 'load' ? img.width >= 1 : false;
      resolve(window.isSupportWebp);
    }
    img.onerror = getResult;
    img.onload = getResult;
    img.src = 'data:image/webp;base64,UklGRiQAAABXRUJQVlA4IBgAAAAwAQCdASoBAAEAAwA0JaQAA3AA/vuUAAA='; //一像素图片
  });
};

isLoadMinHeaderWebp();

const browser = {
  ...checkVersion(),
};

const _typeof =
  typeof Symbol === 'function' && typeof Symbol.iterator === 'symbol'
    ? function (obj: any) {
        return typeof obj;
      }
    : function (obj: any) {
        return obj && typeof Symbol === 'function' && obj.constructor === Symbol && obj !== Symbol.prototype ? 'symbol' : typeof obj;
      };
//去盲盒记录
function openRoomBlindBoxRecord() {
  if (browser.ios && window.webkit) {
    window.webkit.messageHandlers.openHomePage.postMessage();
  } else if (browser.android) {
    if (window.androidJsObj && (typeof window.androidJsObj === 'undefined' ? 'undefined' : _typeof(window.androidJsObj)) === 'object') {
      window.androidJsObj.openRoomBlindBoxRecord();
    }
  }
}

/**
 * 时间格式化
 * @param {Date|string|number} time 时间
 * @param {string} cFormat 格式 默认 -> {y}-{m}-{d} {h}:{i}:{s}
 * @returns
 */
const parseTime = (time: string | number, cFormat: string) => {
  // if (arguments.length === 0 || !time) {
  //   return null;
  // }
  const format = cFormat || '{y}-{m}-{d} {h}:{i}:{s}';
  let date;
  if (typeof time === 'object') {
    date = time;
  } else {
    if (typeof time === 'string') {
      if (/^[0-9]+$/.test(time)) {
        // support "1548221490638"
        time = parseInt(time);
      } else {
        // support safari
        // https://stackoverflow.com/questions/4310953/invalid-date-in-safari
        time = time.replace(new RegExp(/-/gm), '/');
      }
    }

    if (typeof time === 'number' && time.toString().length === 10) {
      time = time * 1000;
    }
    date = new Date(time);
  }
  const formatObj: any = {
    y: date.getFullYear(),
    m: date.getMonth() + 1,
    d: date.getDate(),
    h: date.getHours(),
    i: date.getMinutes(),
    s: date.getSeconds(),
  };
  const time_str = format.replace(/{([ymdhisa])+}/g, (result, key) => {
    const value = formatObj[key];
    return value.toString().padStart(2, '0');
  });
  return time_str;
};

/**
 * 获取个人排行
 * @param {Number} seqNo 当前排行
 * @param {Number} maxNo 最大排行
 */
function getSelfRank(seqNo: number, maxNo: number) {
  return seqNo > 0 && seqNo <= maxNo ? seqNo : maxNo + '+';
}
// 图片处理
export const handlePicUrl = (picUrl: string) => {
  if (typeof picUrl !== 'string' || !picUrl) return '';
  if (picUrl.includes(import.meta.env.VITE_APP_QNURL)) {
    // 七牛云
    const cs = '?imageslim';
    const cs2 = '|imageView2/1/w/200/h/200/format/webp';
    let url = picUrl.includes(cs) ? picUrl + cs2 : picUrl + cs + cs2;
    if (window.isSupportWebp) url = url.replace('webp', '');
    return url;
  } else {
    return picUrl;
  }
};
function _copyToClipboard(content: any, callback?: any) {
  let textarea = document.createElement('textarea');
  textarea.style.position = 'absolute';
  textarea.style.left = '-1000px';
  textarea.style.top = '-1000px';
  textarea.value = content;
  textarea.readOnly = true;
  document.body.appendChild(textarea);
  textarea.select();
  textarea.setSelectionRange(0, textarea.value.length);
  let result = false;
  try {
    result = document.execCommand('copy');
    callback && callback();
  } catch (e) {}
  document.body.removeChild(textarea);
  //@ts-expect-error This line intentionally assigns null to textarea.
  textarea = null;
  return result;
}
// 图片预加载
export const peviewImage = async (src: string | string[]) => {
  let perviewImage: string[] = [];
  if (typeof src === 'string') {
    perviewImage.push(src);
  } else {
    perviewImage = src;
    const isWebp = await isLoadMinHeaderWebp();
    if (isWebp) {
      const newUrl = perviewImage.filter((item) => item.split('?')[0].split('.')[item.split('.').length - 1] === 'webp');
      perviewImage.forEach((item) => {
        let itemPrefix = item.split(window.location.origin)[1].split('/')[4];
        itemPrefix = itemPrefix.split(`.${[itemPrefix.split('.')[itemPrefix.split('.').length - 1]]}`)[0];
        if (!`${newUrl}`.includes(itemPrefix) && item.split('.')[item.split('.').length - 1] !== 'webp') {
          newUrl.push(item);
        }
      });
      perviewImage = newUrl;
    } else {
      const newUrl = perviewImage.filter((item) => item.split('.')[item.split('.').length - 1] !== 'webp');
      perviewImage = newUrl;
    }
  }
  const loadImage = (src: string) => {
    const img = new Image();
    img.onload = function () {
      console.log(`加载成功：${src}`);
    };
    img.src = src;
  };
  perviewImage.forEach((item) => {
    loadImage(item);
  });
};
/**
 * 时间格式处理
 * @param {*} formatter 时间格式
 * @returns
 */
const _formatterNormalize = (formatter: string | Function) => {
  if (typeof formatter === 'function') {
    return formatter;
  }

  const formatterFunc = (dateInfo: any) => {
    const { yyyy, MM, dd, HH, mm, ss, ms } = dateInfo;
    return formatter.replace(/yyyy/g, yyyy).replace(/MM/g, MM).replace(/dd/g, dd).replace(/HH/g, HH).replace(/mm/g, mm).replace(/ss/g, ss).replace(/ms/g, ms);
  };
  return formatterFunc;
};

/**
 * 根据日期或时间戳获取年月日时分秒
 * @param {*} time 日期或时间戳
 * @param {*} formatter 需要展示的时间格式
 * @param {*} isPad 是否补0
 * @returns
 */
export const formatterTime = (time: number | string | Date, formatter: string | Function = 'yyyy-MM-dd', isPad: boolean = true) => {
  if (!time) return;
  if (typeof time === 'string') {
    if (/^[0-9]+$/.test(time)) {
      // support "1548221490638"
      time = parseInt(time);
    } else {
      // support safari
      // https://stackoverflow.com/questions/4310953/invalid-date-in-safari
      time = time.replace(new RegExp(/-/gm), '/');
    }
  }
  if (typeof time === 'number' && time.toString().length === 10) {
    time = time * 1000;
  }
  const date = new Date(time);
  formatter = _formatterNormalize(formatter);
  const dateInfo: any = {
    yyyy: date.getFullYear().toString(),
    MM: (date.getMonth() + 1).toString(),
    dd: date.getDate().toString(),
    HH: date.getHours().toString(),
    mm: date.getMinutes().toString(),
    ss: date.getSeconds().toString(),
    ms: date.getMilliseconds().toString(),
  };
  const _pad = (prop: keyof any, len: number) => {
    dateInfo[prop] = dateInfo[prop]?.padStart(len, '0');
  };
  if (isPad) {
    _pad('yyyy', 4);
    _pad('MM', 2);
    _pad('dd', 2);
    _pad('HH', 2);
    _pad('mm', 2);
    _pad('ss', 2);
    _pad('ms', 3);
  }
  const result = formatter(dateInfo);
  return result;
};

// 元素过渡动画
export const playAnimation = (el: HTMLElement, name: string, duration: number = 1) => {
  // 加nextTick是因为如果元素上有动态绑定class会冲突
  nextTick(() => {
    const baseClass = 'animate__animated';
    const animClass = `animate__${name}`;

    // 移除旧动画类
    el.classList.remove(baseClass);
    el.classList.forEach((cls) => {
      if (cls.startsWith('animate__')) el.classList.remove(cls);
    });

    // 强制重绘（确保重复动画生效）
    void el.offsetWidth;

    // 添加动画类
    el.classList.add(baseClass, animClass);
    el.style.setProperty('--animate-duration', `${duration}s`);

    // 动画结束后清除 class
    const onEnd = () => {
      el.classList.remove(baseClass, animClass);
      el.removeEventListener('animationend', onEnd);
    };
    el.addEventListener('animationend', onEnd);
  });
};
export default {
  getSelfRank,
  checkVersion,
  info,
  browser,
  openRoomBlindBoxRecord,
  parseTime,
  handlePicUrl,
  _copyToClipboard,
  setKM,
  formatterTime,
  playAnimation,
};
